import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { blogService } from '../../../services/blogService';

const Analytics = () => {
  const [stats, setStats] = useState({
    totalPosts: 0,
    publishedPosts: 0,
    draftPosts: 0,
    totalViews: 0,
    totalComments: 0,
    categoriesCount: 0
  });
  const [recentPosts, setRecentPosts] = useState([]);
  const [topPosts, setTopPosts] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('📊 Loading blog analytics...');

      // Fetch all posts and categories
      const [posts, categories, comments] = await Promise.all([
        blogService?.getAllPosts(),
        blogService?.getAllCategories(),
        blogService?.getAllComments()
      ]);

      console.log('📊 Analytics data loaded:', { 
        postsCount: posts?.length, 
        categoriesCount: categories?.length, 
        commentsCount: comments?.length 
      });

      // Calculate basic stats
      const publishedPosts = posts?.filter(p => p?.isPublished) || [];
      const draftPosts = posts?.filter(p => !p?.isPublished) || [];
      const totalViews = posts?.reduce((sum, post) => sum + (post?.viewCount || 0), 0);

      setStats({
        totalPosts: posts?.length || 0,
        publishedPosts: publishedPosts?.length || 0,
        draftPosts: draftPosts?.length || 0,
        totalViews,
        totalComments: comments?.length || 0,
        categoriesCount: categories?.length || 0
      });

      // Get recent posts (last 5)
      const recent = posts?.slice(0, 5) || [];
      setRecentPosts(recent);

      // Get top posts by views
      const top = [...posts]
        ?.sort((a, b) => (b?.viewCount || 0) - (a?.viewCount || 0))
        ?.slice(0, 5) || [];
      setTopPosts(top);

      // Calculate category statistics
      const categoryMap = {};
      posts?.forEach(post => {
        if (post?.category) {
          const catId = post?.category?.id;
          if (!categoryMap?.[catId]) {
            categoryMap[catId] = {
              name: post?.category?.name,
              color: post?.category?.color,
              count: 0,
              views: 0
            };
          }
          categoryMap[catId].count++;
          categoryMap[catId].views += post?.viewCount || 0;
        }
      });

      const categoryStatsArray = Object.values(categoryMap)
        ?.sort((a, b) => b?.count - a?.count) || [];
      setCategoryStats(categoryStatsArray);

      console.log('✅ Blog analytics loaded successfully');

    } catch (err) {
      console.error('❌ Failed to load analytics:', err);
      setError(err?.message || 'İstatistikler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center mb-2">
          <Icon name="AlertCircle" className="w-5 h-5 text-red-600 mr-2" />
          <h3 className="text-red-800 font-semibold">Hata</h3>
        </div>
        <p className="text-red-700">{error}</p>
        <button
          onClick={loadAnalytics}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Total Posts */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Toplam Yazı</p>
              <p className="text-3xl font-bold mt-2">{stats?.totalPosts}</p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <Icon name="FileText" className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* Published Posts */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Yayında</p>
              <p className="text-3xl font-bold mt-2">{stats?.publishedPosts}</p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <Icon name="CheckCircle" className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* Draft Posts */}
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-medium">Taslak</p>
              <p className="text-3xl font-bold mt-2">{stats?.draftPosts}</p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <Icon name="Edit" className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* Total Views */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Toplam Görüntülenme</p>
              <p className="text-3xl font-bold mt-2">{stats?.totalViews?.toLocaleString('tr-TR')}</p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <Icon name="Eye" className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* Total Comments */}
        <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-pink-100 text-sm font-medium">Toplam Yorum</p>
              <p className="text-3xl font-bold mt-2">{stats?.totalComments}</p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <Icon name="MessageCircle" className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-100 text-sm font-medium">Kategori</p>
              <p className="text-3xl font-bold mt-2">{stats?.categoriesCount}</p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <Icon name="Tag" className="w-8 h-8" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Posts */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center">
              <Icon name="Clock" className="w-5 h-5 mr-2 text-primary" />
              Son Yazılar
            </h3>
          </div>
          <div className="space-y-3">
            {recentPosts?.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">Henüz yazı yok</p>
            ) : (
              recentPosts?.map((post) => (
                <div key={post?.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  {post?.imageUrl && (
                    <img
                      src={post?.imageUrl}
                      alt={post?.title}
                      className="w-12 h-12 rounded object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{post?.title}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                        post?.isPublished ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {post?.isPublished ? 'Yayında' : 'Taslak'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(post?.createdAt)?.toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center text-gray-500 text-xs flex-shrink-0">
                    <Icon name="Eye" className="w-4 h-4 mr-1" />
                    {post?.viewCount || 0}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Top Posts by Views */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center">
              <Icon name="TrendingUp" className="w-5 h-5 mr-2 text-primary" />
              En Çok Görüntülenen
            </h3>
          </div>
          <div className="space-y-3">
            {topPosts?.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">Henüz yazı yok</p>
            ) : (
              topPosts?.map((post, index) => (
                <div key={post?.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  {post?.imageUrl && (
                    <img
                      src={post?.imageUrl}
                      alt={post?.title}
                      className="w-12 h-12 rounded object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{post?.title}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      {post?.category && (
                        <span
                          className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full"
                          style={{ backgroundColor: `${post?.category?.color}20`, color: post?.category?.color }}
                        >
                          {post?.category?.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center text-gray-900 font-semibold text-sm flex-shrink-0">
                    <Icon name="Eye" className="w-4 h-4 mr-1 text-primary" />
                    {post?.viewCount || 0}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Category Statistics */}
      {categoryStats?.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center">
              <Icon name="BarChart" className="w-5 h-5 mr-2 text-primary" />
              Kategori İstatistikleri
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categoryStats?.map((category) => (
              <div key={category?.name} className="border-2 border-gray-100 rounded-lg p-4 hover:border-primary transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <span
                    className="inline-flex px-3 py-1 text-sm font-semibold rounded-full"
                    style={{ backgroundColor: `${category?.color}20`, color: category?.color }}
                  >
                    {category?.name}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div>
                    <p className="text-xs text-gray-500">Yazı Sayısı</p>
                    <p className="text-lg font-bold text-gray-900">{category?.count}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Görüntülenme</p>
                    <p className="text-lg font-bold text-gray-900">{category?.views?.toLocaleString('tr-TR')}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;